import React from 'react';
import { Svg, Path, View, Text } from '@react-pdf/renderer';
import { convertNumberToDesiredFormat } from 'helpers/transformHelpers';
import { OperationType } from 'components/RecordsListView/constants';
import { FlexTableProps } from '../../components/FlexTable/types';
import { displayText } from '../formatHelpers';
import { convertGeometryToSvgPath } from '../mapHelpers';
import { Locale } from '../../locales';
import PropertiesTableFooter from '../../components/PropertiesTableFooter';
import ActualAcresLabel from '../../components/ActualAcresLabel';

const getFieldsHeaderRow = (locale: Locale): FlexTableProps['tableRows'][0] => {
  return [
    { label: '', styles: { width: '5%' } },
    { label: locale.properties.farm, styles: { width: '15.12%' } },
    { label: locale.properties.field, styles: { width: '15.12%' } },
    { label: locale.properties.crop_zone, styles: { width: '15.89%' } },
    { label: locale.properties.crop, styles: { width: '12.60%' } },
    {
      label: locale.properties.center_lat_long,
      styles: { width: '19.77%' }
    },
    {
      label: locale.properties.applied_acreage,
      styles: { width: '16.51%', textAlign: 'right' }
    }
  ];
};

// eslint-disable-next-line import/prefer-default-export
export const getFieldTableRows = (
  fields: any[],
  locale: Locale,
  totalAppliedArea: number,
  operationType: OperationType = OperationType.APPLICATION
): FlexTableProps['tableRows'] => {
  const isWorkOrder = operationType === OperationType.WORK_ORDER;
  const fieldRows: FlexTableProps['tableRows'] = fields.map(field => {
    // Get field boundary icon
    const getFieldIcon = () => {
      if (!field.fieldGeometry) {
        return null;
      }

      const pathData = convertGeometryToSvgPath(field.fieldGeometry);

      if (!pathData) {
        return null;
      }

      return (
        // @ts-ignore - library issue
        <Svg
          width="20"
          height="20"
          viewBox="0 0 34 34"
          style={{ width: 20, height: 20 }}
        >
          <Path
            d={pathData}
            fill="#C2C7D0"
            stroke="#4D5165"
            strokeWidth="0.5"
          />
        </Svg>
      );
    };

    const icon = {
      key: 'icon',
      label: getFieldIcon(),
      styles: {
        width: '5%',
        paddingTop: 2,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start'
      }
    };

    const farmName = {
      key: 'farmName',
      label: displayText(field.farmName),
      styles: { width: '15.12%' }
    };

    const fieldName = {
      key: 'fieldName',
      label: displayText(field.fieldName),
      styles: { width: '15.12%' }
    };

    const cropZoneName = {
      key: 'cropZoneName',
      label: displayText(field.cropZoneName),
      styles: { width: '15.89%' }
    };

    const cropName = {
      key: 'cropName',
      label: displayText(field.cropName),
      styles: { width: '12.60%' }
    };

    const latlong = {
      key: 'latlong',
      label: field.latlong
        ? `${convertNumberToDesiredFormat(
            field.latlong[1],
            'rounded',
            5
          )}, ${convertNumberToDesiredFormat(field.latlong[0], 'rounded', 5)}`
        : displayText(null),
      styles: { width: '19.77%' }
    };

    const area = {
      key: 'area',
      label: (
        <View>
          <Text>
            {convertNumberToDesiredFormat(field.areaValue || 0, 'fixed')} ac
          </Text>
          {isWorkOrder && (
            <ActualAcresLabel label={locale.properties.actual_acres} />
          )}
        </View>
      ),
      styles: { width: '16.09%', textAlign: 'right' }
    };

    return [icon, farmName, fieldName, cropZoneName, cropName, latlong, area];
  });

  const totalAreaRow = [
    {
      label: (
        <PropertiesTableFooter
          title={locale.properties.total_applied_acreage}
          totalAppliedAcreage={totalAppliedArea}
          operationType={operationType}
          actualAcresLabel={locale.properties.actual_acres}
        />
      ),
      styles: { width: '100%', padding: 0 },
      isCustom: true
    }
  ];

  return [getFieldsHeaderRow(locale), ...fieldRows, totalAreaRow];
};
